<script lang="ts" setup>
import { getSoortRelatieDisplayText } from '@services/crm-public/helper';
import { relatieQueries } from '@services/crm-public/queries/Relatie';
import { ModalType } from '@shared/component/types';
import VModal from '@shared/component/VModal/VModal.vue';
import { useQuery } from '@tanstack/vue-query';
import { parseAddressAsString } from '@tools/parsers';
import { useConfirmDialog } from '@vueuse/core';
import { computed, ref, toRefs, watch } from 'vue';
import VRadioOptions from '@shared/form/VRadioOptions/VRadioOptions.vue';

const props = defineProps<{ kvkNummer: string }>();
const emit = defineEmits<{ relatienummerSelected: [relatienummer: number] }>();
const selectedRelatienummer = ref<number | undefined>();
const { kvkNummer } = toRefs(props);

const {
  isRevealed,
  reveal,
  confirm,
  cancel,
  onConfirm: onBestaandeRelatieConfirmed,
} = useConfirmDialog();

const showBestaandeRelatieSelectieModal = computed(() => !!props.kvkNummer);

const { data: bestaandRelatieOpBasisVanKvk } = useQuery({
  ...relatieQueries.search({ kvknummer: kvkNummer }),
  select: (r) => r.result,
  enabled: showBestaandeRelatieSelectieModal,
});

const radioOptions = computed(() =>
  bestaandRelatieOpBasisVanKvk.value
    ? bestaandRelatieOpBasisVanKvk.value.map((relatie) => ({
        data: relatie,
        value: relatie.relatienummer,
      }))
    : [],
);

watch(
  bestaandRelatieOpBasisVanKvk,
  async (relaties) => {
    if ((relaties ?? []).length === 0) return;

    if (!showBestaandeRelatieSelectieModal.value) return;

    await reveal();
  },
  { immediate: true },
);

onBestaandeRelatieConfirmed(() => {
  if (selectedRelatienummer.value) {
    emit('relatienummerSelected', selectedRelatienummer.value);
  }
});
</script>

<template>
  <VModal
    :disabled="!selectedRelatienummer"
    :title="`Reeds bekende relatie${(bestaandRelatieOpBasisVanKvk ?? []).length > 1 ? '\'s' : ''} gevonden!`"
    :is-active="isRevealed"
    @close-modal="cancel"
    @confirm-action="confirm"
    @deny-action="cancel"
    confirm-button-text="Kies bestaande relatie"
    :type="ModalType.Success"
    cancel-button-text="Verder met nieuwe relatie"
  >
    <div class="flex flex-col gap-2">
      <div class="mb-2 flex flex-col">
        <p>De volgende relatie('s) zijn al bekend bij KVK-nummer: {{ kvkNummer }}.</p>
        <p>Kies een bestaande relatie om verder te gaan.</p>
      </div>

      <VRadioOptions v-model="selectedRelatienummer" :options="radioOptions">
        <template #id="{ relatienummer }">
          {{ relatienummer }}
        </template>

        <template #default="{ naam }">
          {{ naam }}
        </template>

        <template #description="{ adres, email, soortRelatie, telefoonnummer }">
          <ul>
            <li v-if="adres">{{ parseAddressAsString(adres) }}</li>
            <li v-if="email || telefoonnummer">
              {{ email ?? '' }}
              {{ email && telefoonnummer ? '&middot;' : '' }}
              {{ telefoonnummer ?? '' }}
            </li>
            <li v-if="getSoortRelatieDisplayText(soortRelatie)">
              {{ getSoortRelatieDisplayText(soortRelatie) }}
            </li>
          </ul>
        </template>
      </VRadioOptions>
    </div>
  </VModal>
</template>
